import React, { useState, useEffect } from "react";
import { Api } from "../../network/api";
import { AlertCircleIcon, AddIcon, DeleteIcon } from "../../assets/icons";
import Button from "../../component/shared/Button";

const PlanModal = ({ isOpen, onClose, onSuccess, plan = null, mode = "create" }) => {
  const isEditMode = mode === "edit" && plan !== null;
  
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    active: true,
    stripe_product_id: "",
    features: [{ name: "", included: true }],
    metadata: {},
    display_order: "",
    allowed_essays: "",
    prices: [
      {
        id: null,
        amount: "",
        currency: "usd",
        interval: "",
        interval_count: "",
        nickname: "",
        stripe_price_id: "",
        active: true,
        display_order: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode && plan && isOpen) {
      setFormData({
        name: plan.name || "",
        description: plan.description || "",
        active: plan.active !== undefined ? plan.active : true,
        stripe_product_id: plan.stripe_product_id || "",
        features: plan.features && plan.features.length > 0 
          ? [...plan.features.map(f => ({ name: f.name || "", included: f.included !== undefined ? f.included : true }))].reverse()
          : [{ name: "", included: true }],
        metadata: plan.metadata || {},
        display_order: plan.display_order !== undefined && plan.display_order !== null ? String(plan.display_order) : "",
        allowed_essays: plan.allowed_essays !== undefined && plan.allowed_essays !== null ? String(plan.allowed_essays) : "",
        prices: plan.prices && plan.prices.length > 0
          ? plan.prices.map(p => ({
              id: p.id || null,
              amount: p.amount !== undefined && p.amount !== null ? String(p.amount) : "",
              currency: "usd",
              interval: p.interval || "",
              interval_count: p.interval_count !== undefined && p.interval_count !== null ? String(p.interval_count) : "",
              nickname: p.nickname || "",
              stripe_price_id: p.stripe_price_id || "",
              active: p.active !== undefined ? p.active : true,
              display_order: p.display_order !== undefined && p.display_order !== null ? String(p.display_order) : "",
            }))
          : [
              {
                id: null,
                amount: "",
                currency: "usd",
                interval: "",
                interval_count: "",
                nickname: "",
                stripe_price_id: "",
                active: true,
                display_order: "",
              },
            ],
      });
    } else if (!isEditMode && isOpen) {
      // Reset form for create mode
      setFormData({
        name: "",
        description: "",
        active: true,
        stripe_product_id: "",
        features: [{ name: "", included: true }],
        metadata: {},
        display_order: "",
        allowed_essays: "",
        prices: [
          {
            amount: "",
            currency: "usd",
            interval: "",
            interval_count: "",
            nickname: "",
            stripe_price_id: "",
            active: true,
            display_order: "",
          },
        ],
      });
      setErrors({});
    }
  }, [plan, isOpen, isEditMode]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(
      () => setNotification({ show: false, type: "", message: "" }),
      5000
    );
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumberChange = (field, value) => {
    // Allow empty string, single 0, or numbers without leading zeros
    if (value === "") {
      handleInputChange(field, "");
    } else if (value === "0") {
      handleInputChange(field, "0");
    } else {
      // Remove leading zeros but keep the number
      const numValue = value.replace(/^0+/, "") || value;
      handleInputChange(field, numValue);
    }
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = {
      ...newFeatures[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      features: newFeatures,
    }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [{ name: "", included: true }, ...prev.features],
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        features: newFeatures,
      }));
    }
  };

  const handlePriceChange = (index, field, value) => {
    const newPrices = [...formData.prices];
    newPrices[index] = {
      ...newPrices[index],
      [field]: value,
    };
    
    // Auto-set interval_count based on interval
    if (field === "interval") {
      if (value === "month") {
        newPrices[index].interval_count = "30";
      } else if (value === "quarter") {
        newPrices[index].interval_count = "90";
      } else if (value === "") {
        // Reset interval_count when interval is cleared
        newPrices[index].interval_count = "";
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      prices: newPrices,
    }));
  };

  const handlePriceNumberChange = (index, field, value) => {
    // Allow empty string, single 0, or numbers without leading zeros
    if (value === "") {
      handlePriceChange(index, field, "");
    } else if (value === "0") {
      handlePriceChange(index, field, "0");
    } else {
      // Remove leading zeros but keep the number
      const numValue = value.replace(/^0+/, "") || value;
      handlePriceChange(index, field, numValue);
    }
  };

  const addPrice = () => {
    setFormData((prev) => ({
      ...prev,
      prices: [
        ...prev.prices,
        {
          id: null,
          amount: "",
          currency: "usd",
          interval: "",
          interval_count: "",
          nickname: "",
          stripe_price_id: "",
          active: true,
          display_order: "",
        },
      ],
    }));
  };

  const removePrice = (index) => {
    if (formData.prices.length > 1) {
      const newPrices = formData.prices.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        prices: newPrices,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Plan name is required";
    }

    if (formData.allowed_essays !== "" && (isNaN(formData.allowed_essays) || parseInt(formData.allowed_essays) < 0)) {
      newErrors.allowed_essays = "Allowed essays must be 0 or greater";
    }

    if (formData.display_order !== "" && (isNaN(formData.display_order) || parseInt(formData.display_order) < 0)) {
      newErrors.display_order = "Display order must be 0 or greater";
    }

    formData.features.forEach((feature, index) => {
      if (!feature.name.trim()) {
        newErrors[`feature_${index}`] = "Feature name is required";
      }
    });

    formData.prices.forEach((price, index) => {
      if (price.amount === "" || isNaN(price.amount) || parseFloat(price.amount) <= 0) {
        newErrors[`price_amount_${index}`] = "Price amount must be greater than 0";
      }
      // Currency is hardcoded to "usd", no validation needed
      if (!price.interval.trim()) {
        newErrors[`price_interval_${index}`] = "Interval is required";
      }
      if (price.interval_count === "" || isNaN(price.interval_count) || parseInt(price.interval_count) <= 0) {
        newErrors[`price_interval_count_${index}`] = "Interval count must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification("error", "Please fix the errors in the form");
      return;
    }

    setLoading(true);

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      active: formData.active,
      stripe_product_id: formData.stripe_product_id.trim() || null,
      features: formData.features.slice().reverse().map((f) => ({
        name: f.name.trim(),
        included: f.included,
      })),
      metadata: formData.metadata,
      display_order: formData.display_order !== "" ? parseInt(formData.display_order) : 0,
      allowed_essays: formData.allowed_essays !== "" ? parseInt(formData.allowed_essays) : 0,
      prices: formData.prices.map((p) => {
        // For create mode, don't include id at all
        // For update mode, include id if it exists
        const pricePayload = isEditMode && p.id !== null && p.id !== undefined
          ? {
              id: p.id,
              amount: parseFloat(p.amount),
              currency: "usd",
              interval: p.interval.trim(),
              interval_count: p.interval_count !== "" ? parseInt(p.interval_count) : 1,
              nickname: p.nickname.trim() || null,
              stripe_price_id: p.stripe_price_id.trim() || null,
              active: p.active,
              display_order: p.display_order !== "" ? parseInt(p.display_order) : 0,
            }
          : {
              amount: parseFloat(p.amount),
              currency: "usd",
              interval: p.interval.trim(),
              interval_count: p.interval_count !== "" ? parseInt(p.interval_count) : 1,
              nickname: p.nickname.trim() || null,
              stripe_price_id: p.stripe_price_id.trim() || null,
              active: p.active,
              display_order: p.display_order !== "" ? parseInt(p.display_order) : 0,
            };
        
        return pricePayload;
      }),
    };

    try {
      let response;
      if (isEditMode) {
        response = await Api.updatePlan(plan.id, payload);
        showNotification("success", "Plan updated successfully!");
      } else {
        response = await Api.createPlan(payload);
        showNotification("success", "Plan created successfully!");
      }
      
      // Reset form only for create mode
      if (!isEditMode) {
        setFormData({
          name: "",
          description: "",
          active: true,
          stripe_product_id: "",
          features: [{ name: "", included: true }],
          metadata: {},
          display_order: "",
          allowed_essays: "",
          prices: [
            {
              id: null,
              amount: "",
              currency: "usd",
              interval: "",
              interval_count: "",
              nickname: "",
              stripe_price_id: "",
              active: true,
              display_order: "",
            },
          ],
        });
        setErrors({});
      }

      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess(response.data);
      }, 2000);
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} plan:`, error);
      showNotification(
        "error",
        error.response?.data?.detail || `Failed to ${isEditMode ? "update" : "create"} plan`
      );
    }

    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    // Reset form when closing
    if (!isEditMode) {
      setFormData({
        name: "",
        description: "",
        active: true,
        stripe_product_id: "",
        features: [{ name: "", included: true }],
        metadata: {},
        display_order: "",
        allowed_essays: "",
        prices: [
          {
            amount: "",
            currency: "usd",
            interval: "",
            interval_count: "",
            nickname: "",
            stripe_price_id: "",
            active: true,
            display_order: "",
          },
        ],
      });
      setErrors({});
    }
    onClose();
  };

  if (!isOpen) return null;
  if (isEditMode && !plan) return null;

  const inputClass = `
    w-full px-4 py-3 border-2 rounded-xl transition-all duration-300
    bg-white text-gray-900 placeholder-gray-500
    border-gray-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20
    focus:outline-none
  `;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl w-full max-w-4xl mx-auto animate-fadeInUp rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {isEditMode ? "Update Plan" : "Create New Plan"}
              </h2>
              <p className="text-red-100 text-sm font-medium mt-1">
                {isEditMode 
                  ? "Update subscription plan details"
                  : "Add a new subscription plan for users"}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-red-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {notification.show && (
          <div
            className={`mx-6 mt-6 p-4 rounded-xl border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-400 text-green-700"
                : "bg-red-50 border-red-400 text-red-700"
            }`}
          >
            <div className="flex items-center space-x-3">
              <AlertCircleIcon width="20" height="20" />
              <span className="font-medium">{notification?.message}</span>
            </div>
          </div>
        )}

        {/* Active Status Indicator - Prominent at top */}
        <div className="mx-6 mt-6 mb-4">
          <div className={`relative rounded-xl p-4 border-2 ${
            formData.active
              ? "bg-gradient-to-r from-green-50 to-green-100 border-green-400"
              : "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-400"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div>
                  <p className={`text-lg font-bold ${
                    formData.active ? "text-green-900" : "text-gray-800"
                  }`}>
                    Plan Status
                  </p>
                </div>
              </div>
              <label className="flex items-center space-x-3 cursor-pointer">
                <span className={`text-sm font-semibold ${
                  formData.active ? "text-green-700" : "text-gray-600"
                }`}>
                  {formData.active ? "Active" : "Inactive"}
                </span>
                <div className={`relative w-14 h-8 rounded-full transition-colors ${
                  formData.active ? "bg-green-500" : "bg-gray-400"
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => handleInputChange("active", e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                    formData.active ? "transform translate-x-6" : ""
                  }`}></div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-8 py-6 space-y-6 flex-1 overflow-y-auto scrollbar-hide"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Basic Information
            </h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`${inputClass} ${errors.name ? "border-red-300" : ""}`}
                placeholder="e.g., Monthly Plan"
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1 flex items-center space-x-2">
                  <AlertCircleIcon width="16" height="16" />
                  <span>{errors.name}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className={`${inputClass} min-h-[100px]`}
                placeholder="Plan description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Allowed Essays
                </label>
                <input
                  type="number"
                  value={formData.allowed_essays}
                  onChange={(e) =>
                    handleNumberChange("allowed_essays", e.target.value)
                  }
                  className={`${inputClass} ${errors.allowed_essays ? "border-red-300" : ""}`}
                  min="0"
                  placeholder="0"
                />
                {errors.allowed_essays && (
                  <p className="text-sm text-red-600 mt-1">{errors.allowed_essays}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    handleNumberChange("display_order", e.target.value)
                  }
                  className={`${inputClass} ${errors.display_order ? "border-red-300" : ""}`}
                  min="0"
                  placeholder="0"
                />
                {errors.display_order && (
                  <p className="text-sm text-red-600 mt-1">{errors.display_order}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Stripe Product ID
              </label>
              <input
                type="text"
                value={formData.stripe_product_id}
                onChange={(e) => handleInputChange("stripe_product_id", e.target.value)}
                className={inputClass}
                placeholder="prod_xxxxx"
              />
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-900">Features</h3>
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <AddIcon width="16" height="16" />
                <span className="text-sm">Add Feature</span>
              </button>
            </div>

            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <input
                    type="text"
                    value={feature.name}
                    onChange={(e) =>
                      handleFeatureChange(index, "name", e.target.value)
                    }
                    className={`${inputClass} ${errors[`feature_${index}`] ? "border-red-300" : ""}`}
                    placeholder="Feature name"
                  />
                  {errors[`feature_${index}`] && (
                    <p className="text-sm text-red-600 mt-1">{errors[`feature_${index}`]}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feature.included}
                      onChange={(e) =>
                        handleFeatureChange(index, "included", e.target.checked)
                      }
                      className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700">Included</span>
                  </label>
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <DeleteIcon width="16" height="16" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Prices */}
          <div className="space-y-4">
            {/* <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
              <button
                type="button"
                onClick={addPrice}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <AddIcon width="16" height="16" />
                <span className="text-sm">Add Price</span>
              </button>
            </div> */}

            {formData.prices.map((price, index) => (
              <div
                key={index}
                className="p-4 bg-gray-50 rounded-lg space-y-4 border-2 border-gray-200"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={price.amount}
                      onChange={(e) =>
                        handlePriceNumberChange(index, "amount", e.target.value)
                      }
                      className={`${inputClass} ${errors[`price_amount_${index}`] ? "border-red-300" : ""}`}
                      min="0"
                      placeholder="0.00"
                    />
                    {errors[`price_amount_${index}`] && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors[`price_amount_${index}`]}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Currency <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value="usd"
                      readOnly
                      disabled
                      className="w-full px-4 py-3 border-2 rounded-xl bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Interval <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={price.interval}
                      onChange={(e) =>
                        handlePriceChange(index, "interval", e.target.value)
                      }
                      className={`${inputClass} ${errors[`price_interval_${index}`] ? "border-red-300" : ""}`}
                    >
                      <option value="">Select interval</option>
                      <option value="month">Month (30 days)</option>
                      <option value="quarter">Quarter (90 days)</option>
                    </select>
                    {errors[`price_interval_${index}`] && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors[`price_interval_${index}`]}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Interval Count <span className="text-red-500">*</span>
                      {price.interval && (price.interval === "month" || price.interval === "quarter") && (
                        <span className="text-xs text-gray-500 ml-2">(Auto-filled)</span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={price.interval_count}
                      onChange={(e) =>
                        handlePriceNumberChange(
                          index,
                          "interval_count",
                          e.target.value
                        )
                      }
                      readOnly={price.interval === "month" || price.interval === "quarter"}
                      className={
                        (price.interval === "month" || price.interval === "quarter")
                          ? `w-full px-4 py-3 border-2 rounded-xl bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300 ${
                              errors[`price_interval_count_${index}`] ? "border-red-300" : ""
                            }`
                          : `${inputClass} ${
                              errors[`price_interval_count_${index}`] ? "border-red-300" : ""
                            }`
                      }
                      min="1"
                      placeholder={price.interval === "month" ? "30" : price.interval === "quarter" ? "90" : "30"}
                    />
                    {errors[`price_interval_count_${index}`] && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors[`price_interval_count_${index}`]}
                      </p>
                    )}
                    
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nickname
                    </label>
                    <input
                      type="text"
                      value={price.nickname}
                      onChange={(e) =>
                        handlePriceChange(index, "nickname", e.target.value)
                      }
                      className={inputClass}
                      placeholder="e.g., Monthly"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Stripe Price ID
                    </label>
                    <input
                      type="text"
                      value={price.stripe_price_id}
                      onChange={(e) =>
                        handlePriceChange(index, "stripe_price_id", e.target.value)
                      }
                      className={inputClass}
                      placeholder="price_xxxxx"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={price.display_order}
                      onChange={(e) =>
                        handlePriceNumberChange(
                          index,
                          "display_order",
                          e.target.value
                        )
                      }
                      className={inputClass}
                      min="0"
                      placeholder="0"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={price.active}
                        onChange={(e) =>
                          handlePriceChange(index, "active", e.target.checked)
                        }
                        className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">Active Price</span>
                    </label>
                  </div>
                </div>

                {formData.prices.length > 1 && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => removePrice(index)}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <DeleteIcon width="16" height="16" />
                      <span className="text-sm">Remove Price</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </form>

        <div className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50/50">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
            >
              {isEditMode ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanModal;

